"use client";
import Link from "next/link";
import Image from "next/image";
import { EmailTwo, Location } from "../svg";
import FooterSocial from "./footer-social";
import logo from "@/assets/img/logo/HTU Logo.svg";
import { footerLinks } from "@/data/footer-links";
import Call from "../svg/call";
import { useTranslation } from "react-i18next";
import { ifArabic, getLocalizedPath } from "@/utils/language";
import { useInView } from "react-intersection-observer";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Form from "./Form";
import { SnackbarProvider } from "notistack";
import { useLanguage } from "@/app/[lang]/client/language-context";

type IProps = { style_2?: boolean };

export default function FooterOne({ style_2 = false }: IProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Intersection Observer for newsletter form
  const { ref: formRef, inView } = useInView({
    triggerOnce: true,
    rootMargin: "0px 0px 200px 0px",
  });

  return (
    <footer>
      <div className="tp-footer-main pt-70 pb-50">
        <div className="container">
          <div className="row">
            {/* Logo & contact */}
            <div className="col-xl-4 col-lg-3 col-sm-6">
              <div
                className={`tp-footer-widget ${
                  style_2 ? "tp-footer-5-col-1" : "tp-footer-col-1"
                } mb-30`}
              >
                <div className="tp-footer-logo mb-20">
                  <Link
                    href={getLocalizedPath("/", language)}
                    aria-label="HTU Home Page"
                  >
                    <Image
                      src={logo}
                      alt="HTU Logo"
                      width={228}
                      height={150}
                      priority
                    />
                  </Link>
                </div>
                <div className="tp-footer-slogan mb-3">
                  {t("footer_slogan")}
                </div>
                <div className="tp-footer-contact-mail mb-3">
                  <Link
                    aria-label="Location"
                    href="https://maps.app.goo.gl/B343oUhjjG7wMhQN9"
                    target="_blank"
                  >
                    <span style={{ marginRight: "10px" }}>
                      <Location />
                    </span>
                    {t("footer_address")}
                  </Link>
                </div>
                <div className="tp-footer-contact-mail mb-3">
                  <Link
                    aria-label="Phone"
                    href="tel:+96265808787"
                    className="number-footer"
                  >
                    <span style={{ marginRight: "6px" }}>
                      <Call />
                    </span>
                    <div className="number-rtl">+962 6 580 8787</div>
                  </Link>
                </div>
                <div className="tp-footer-contact-mail">
                  <Link aria-label="Email" href="mailto:info@htu.edu.jo">
                    <span style={{ marginRight: "10px" }}>
                      <EmailTwo />
                    </span>
                    info@htu.edu.jo
                  </Link>
                </div>
              </div>
            </div>

            {/* About links */}
            <div className="col-xl-2 col-lg-3 col-sm-6">
              <div
                className={`tp-footer-widget ${
                  style_2 ? "tp-footer-5-col-2" : "tp-footer-col-2"
                } mb-30`}
              >
                <h3 className="tp-footer-widget-title mb-20">
                  {t("footer_about_title")}
                </h3>
                <ul className="tp-footer-widget-link">
                  {footerLinks.link_one.map((link) => (
                    <li key={link.id}>
                      {" "}
                      <Link
                        aria-label={link.title || "Footer link"}
                        href={
                          link.link?.startsWith("http")
                            ? link.link
                            : getLocalizedPath(link.link || "#", language)
                        }
                      >
                        {ifArabic(link.titleAr, link.title)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-xl-2 col-lg-2 col-sm-4">
              <div
                className={`tp-footer-widget ${
                  style_2 ? "tp-footer-5-col-3" : "tp-footer-col-3"
                } mb-30`}
              >
                <h3 className="tp-footer-widget-title mb-20">
                  {t("footer_quick_links_title")}
                </h3>
                <ul className="tp-footer-widget-link">
                  {footerLinks.link_two.map((link) => (
                    <li key={link.id}>
                      {" "}
                      <Link
                        href={
                          link.link?.startsWith("http")
                            ? link.link
                            : getLocalizedPath(link.link || "#", language)
                        }
                        aria-label={link.title}
                        target={
                          [2, 4, 5].includes(link.id) ? "_blank" : undefined
                        }
                      >
                        {ifArabic(link.titleAr, link.title)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Newsletter Form (lazy‑loaded recaptcha) */}
            <div className="col-xl-4 col-lg-4 col-sm-8">
              <div
                className={`tp-footer-widget ${
                  style_2 ? "tp-footer-5-col-4" : "tp-footer-col-4"
                } mb-30`}
              >
                <h3 className="tp-footer-widget-title mb-20">
                  {t("footer_newsletter_title")}
                </h3>

                <div className="tp-footer-newsletter-wrap" ref={formRef}>
                  <p>{t("footer_newsletter_text")}</p>

                  {inView ? (
                    <GoogleReCaptchaProvider
                      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                      scriptProps={{
                        async: true,
                        defer: true,
                        appendTo: "head",
                      }}
                    >
                      <SnackbarProvider maxSnack={3}>
                        <Form style_2={style_2} />
                      </SnackbarProvider>
                    </GoogleReCaptchaProvider>
                  ) : (
                    <p>Loading newsletter form…</p>
                  )}

                  <div className="tp-footer-newsletter-social">
                    <FooterSocial />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={style_2 ? "tp-footer-5-bottom" : "tp-footer-bottom"}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12 d-flex justify-content-center align-items-center">
              <div className="tp-footer-copyright d-flex">
                {" "}
                {t("footer_copyright_1")}{" "}
                <div
                  className={ifArabic("letter-spacing-5", "")}
                  style={{ margin: "0 2px 0 4px" }}
                >
                  {new Date().getFullYear()}{" "}
                </div>{" "}
                {t("footer_copyright_2")}{" "}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
